import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SecurityContext,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import {
  CanvasElementPropertiesWithData,
  getActiveBinding,
  getBackground,
  makeBorderStyles,
} from '@blaze-shared/elements-domain';
import { urlUtils } from '@blaze-shared/platform-util';
import { DatasetRow } from '@blaze-shared/schema-domain';
import { ElementLoadingIndicatorComponent } from '@blaze-shared/shared-ui';
import { isNullish } from '@blaze-shared/shared-util';

import { ElementHeaderComponent } from '../../components/element-header/element-header.component';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { PdfViewerWrapperComponent } from './pdf-viewer-wrapper/pdf-viewer-wrapper.component';
import { PdfViewerService } from './pdf-viewer.service';

@Component({
  selector: 'be-pdf-viewer',
  imports: [
    ElementHeaderComponent,
    ElementLoadingIndicatorComponent,
    MatIconModule,
    NgStyle,
    PdfViewerWrapperComponent,
  ],
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfViewerComponent
  extends CanvasElementBaseComponent<'pdfViewer'>
  implements OnInit, OnChanges, OnDestroy
{
  private ds = inject(DomSanitizer);
  private pdfViewerService = inject(PdfViewerService);

  @Input() value?: string | DatasetRow;

  private isValidUrl = true;

  protected labelEnabled = signal<boolean>(false);
  protected isPdfLoading = signal<boolean>(false);
  protected label = signal<string>('');
  protected errorMessage = signal<string>('');

  // PDF Viewer settings
  protected enableDownload = signal<boolean>(true);
  protected enablePrint = signal<boolean>(true);
  protected filename = signal<string>('');
  protected pdfSrc = signal<string>('');

  protected pdfBorderStyles = signal<Record<string, string>>({});
  protected zoomLevels = [
    'auto',
    'page-actual',
    'page-fit',
    'page-width',
    0.2,
    0.25,
    0.33,
    0.5,
    0.75,
    1,
    1.25,
    1.5,
    2,
    2.5,
    3,
    3.5,
    4,
  ];

  /**
   * Checks whether an instance of PDF Viewer has already been instantiated. pdf.js pollutes the global
   * namespace so we can allow only one instance of the viewer at the time.
   */
  get hasOtherPdfViewerInstance(): boolean {
    return (
      !!this.pdfViewerService.pdfViewers.size &&
      !this.pdfViewerService.pdfViewers.has(this.elementId)
    );
  }
  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Value
    const pdfUpdated =
      changes?.['value'] ||
      changes?.['props']?.currentValue?.content?.pdf?.url ||
      changes?.['bindingResults'];

    if (pdfUpdated && !this.hasOtherPdfViewerInstance) {
      this.updatePdfUrl();
    }

    if (this.hasOtherPdfViewerInstance) {
      this.errorMessage.set('Another PDF is already opened');
    }

    // Props
    if (changes?.['props']) {
      const { content } = this.props ?? {};
      const { title } = content ?? {};
      this.label.set(content?.title?.label || '');
      this.enableDownload.set(!!content?.pdf?.downloadEnabled);
      this.enablePrint.set(!!content?.pdf?.printEnabled);
      this.labelEnabled.set(title?.enabled ?? false);
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    if (!this.hasOtherPdfViewerInstance) {
      this.pdfViewerService.addInstance(this.elementId);
    }
    this.pdfSrc.set(this.hasOtherPdfViewerInstance ? '' : this.pdfSrc() || '');
  }

  ngOnDestroy(): void {
    this.pdfViewerService.deleteInstance(this.elementId);
  }

  onPdfLoaded(pagesCount: number): void {
    this.isPdfLoading.set(pagesCount < 0);
    this.errorMessage.set('');
  }

  onPdfLoadingFailed(event: Error): void {
    this.isPdfLoading.set(false);
    this.errorMessage.set(event.message);
  }

  onPdfLoadingStarts(): void {
    this.isPdfLoading.set(true);
    this.errorMessage.set('');
  }

  private updatePdfUrl(): void {
    const { data } = (this.props as CanvasElementPropertiesWithData) || {};

    const activeBinding = getActiveBinding(data, 'value');
    const isBoundToData = !!activeBinding?.enabled && !!activeBinding?.dataSource;
    const bindingKey = activeBinding?.bindings?.value;

    // Extract URL using binding/data information
    let url = '';
    const value = Array.isArray(this.value) ? this.value[0] : this.value;
    if (typeof value === 'string' && value) {
      // For string values, assume they contain PDF URL.
      url = value;
    } else if (typeof value === 'object' && !isNullish(value) && isBoundToData && bindingKey) {
      const columnName = this.schemaService.getColumnName(bindingKey) ?? '';
      // For objects, use bindings
      url = value[bindingKey] || value[columnName];
    } else if (!bindingKey || !isBoundToData) {
      // If a binding key is not specified, use statically specified URL
      url = this.props?.content?.pdf?.url || '';
    }

    // Process found URL
    if (!url?.startsWith) {
      return;
    }
    if (!url?.startsWith('https://') && !url?.startsWith('http://')) {
      url = `https://${url}`.trim();
    }
    url = this.ds.sanitize(SecurityContext.URL, url) || '';
    this.isValidUrl = urlUtils.isValidUrl(url);

    if (this.isValidUrl) {
      this.pdfSrc.set(url);
      this.filename.set(url.split('/').pop() || '');
    } else {
      this.errorMessage.set('No PDF found');
      this.pdfSrc.set('');
    }
  }

  private updateStyles(): void {
    const { style, content } = this.props ?? {};
    const { label, border, titleBlock } = style || {};
    if (!style || !label || !border || !titleBlock) {
      return;
    }

    // PDF border
    const pdfBorderStyles: Record<string, string> = {
      background: getBackground(border.outline),
      color: label.color.color1,
      ...makeBorderStyles(border.outline, border.width),
    };
    if (content?.title?.iconPosition === 'right') {
      pdfBorderStyles['flex-direction'] = `row-reverse`;
    }
    if (border.radius !== '0px') {
      pdfBorderStyles['border-radius'] = border.radius;
    }
    this.pdfBorderStyles.set(pdfBorderStyles);
  }
}
